import { useState } from 'react';
import { hotTopics } from '@/mocks/videos';
import { Link } from 'react-router-dom';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setStatus('success');
      setMessage('感谢订阅！');
      setEmail('');
      return;
    }

    setStatus('loading');

    try {
      const payload = new FormData();
      payload.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d9qq916e3oq4jqumton0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email }).toString(),
      });

      const responseText = await response.text();

      if (response.ok) {
        setStatus('success');
        setMessage('订阅成功！感谢您的关注，我们会将最新视频动态发送到您的邮箱。');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <section className="py-14 md:py-20 bg-accent-500">
        <div className="px-4 md:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto rounded-full bg-background-50/20 text-background-50 mb-6">
              <i className="ri-mail-send-line text-2xl md:text-3xl"></i>
            </span>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 dark:text-foreground-950">
              订阅更新
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-50/80 dark:text-foreground-800 max-w-lg mx-auto leading-relaxed">
              第一时间获取狗头萝莉最新视频更新通知，精彩内容不再错过
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="newsletter-subscription" className="mt-8">
              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入您的邮箱地址"
                  required
                  className="w-full px-5 py-3 bg-background-50 text-foreground-950 text-sm rounded-full placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-50/50"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full sm:w-auto px-8 py-3 bg-foreground-950 hover:bg-foreground-800 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === 'loading' ? '提交中...' : '立即订阅'}
                </button>
              </div>

              {status === 'success' && (
                <p className="mt-4 text-sm text-background-50 dark:text-foreground-900 font-medium">{message}</p>
              )}
              {status === 'error' && (
                <p className="mt-4 text-sm text-background-50 dark:text-foreground-900 font-medium">{message}</p>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-100">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                热门话题
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                粉丝最关注的热门内容和深度文章
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {hotTopics.map((topic) => (
                <Link
                  key={topic.id}
                  to={`/article/${topic.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={topic.thumbnail}
                      alt={topic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                      {topic.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {topic.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-eye-line"></i>
                        </span>
                        {topic.views}
                      </span>
                      <span>{topic.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}